// Session Storage Utilities
// Centralized session management for Remember Me functionality

const SESSION_EXPIRY_DAYS = 10;

export interface SessionData {
  email: string;
  expires_at: string;
}

/**
 * Get the appropriate storage based on rememberMe preference
 */
export const getStorage = (rememberMe: boolean): Storage => {
  return rememberMe ? localStorage : sessionStorage;
};

/**
 * Save session data to appropriate storage
 */
export const saveSession = (
  key: string,
  data: object,
  rememberMe: boolean,
  expiryDays: number = SESSION_EXPIRY_DAYS
): void => {
  const storage = getStorage(rememberMe);
  const sessionData = {
    ...data,
    expires_at: new Date(Date.now() + expiryDays * 24 * 60 * 60 * 1000).toISOString(),
    remember_me: rememberMe,
  };
  
  // Encode as base64 for basic obfuscation
  const encoded = btoa(JSON.stringify(sessionData));
  storage.setItem(key, encoded);
  
  // If using sessionStorage, also clear any old localStorage version
  if (!rememberMe) {
    localStorage.removeItem(key);
  }
};

/**
 * Get session from storage (checks both session and local storage)
 * Returns null if session doesn't exist or is expired
 */
export const getSession = <T extends SessionData>(key: string): (T & { remember_me?: boolean }) | null => {
  // Check sessionStorage first (current browser session)
  let stored = sessionStorage.getItem(key);
  let fromLocalStorage = false;
  
  // If not in sessionStorage, check localStorage
  if (!stored) {
    stored = localStorage.getItem(key);
    fromLocalStorage = true;
  }
  
  if (!stored) return null;
  
  try {
    const session = JSON.parse(atob(stored)) as T & { remember_me?: boolean };
    
    // Check if session is expired
    if (new Date(session.expires_at) < new Date()) {
      clearSession(key);
      return null;
    }
    
    return session;
  } catch {
    // Invalid session data, clear it
    if (fromLocalStorage) {
      localStorage.removeItem(key);
    } else {
      sessionStorage.removeItem(key);
    }
    return null;
  }
};

/**
 * Clear session from both storage types
 */
export const clearSession = (key: string): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

/**
 * Save session from a base64 encoded token (used with edge function responses)
 */
export const saveSessionFromToken = <T extends SessionData>(
  key: string,
  token: string,
  rememberMe: boolean
): (T & { remember_me?: boolean }) | null => {
  try {
    const storage = getStorage(rememberMe);
    
    // Parse the token to validate it
    const data = JSON.parse(atob(token)) as T;
    
    // If rememberMe is true, update expiry to 10 days
    if (rememberMe) {
      data.expires_at = new Date(Date.now() + SESSION_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString();
    } else {
      // For session storage, set a short expiry as a fallback
      // (session storage will be cleared when browser closes anyway)
      data.expires_at = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); // 1 day
    }
    
    // Re-encode with potentially updated expiry
    const encodedData = { ...data, remember_me: rememberMe };
    const encoded = btoa(JSON.stringify(encodedData));
    storage.setItem(key, encoded);
    
    // Clear from the other storage
    if (rememberMe) {
      sessionStorage.removeItem(key);
    } else {
      localStorage.removeItem(key);
    }
    
    return encodedData;
  } catch {
    return null;
  }
};
